import React, { FC, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Card,
  IconButton,
  TableCell,
  TableRow,
  TableContainer,
  DialogContent,
  Dialog,
  Button,
  DialogTitle,
  DialogActions,
  CircularProgress,
  TableBody,
  Paper,
  Table,
  TableHead
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import { API } from '../../../config/config';
import {
  ActionCell,
  DynamicTable,
  TableSearch
} from '../../../components/DynamicTable/DynamicTable';
import { useUpdateModalStore } from '../../../zustandStore/store.update';
import toast from 'react-hot-toast';
import { descendingComparator } from '../../../Utils';
import { useAuth } from 'src/hooks/useAuth';
import LogoSpinner from 'src/components/LogoSpinner';
import { CreateAttributeForm } from './CreateAttributeForm';
import { CreateAttributeValueForm } from './CreateAttributeValueForm';

export const SourceActions = {
  ATTRIBUTE_CREATE: 'attribute-create',
  ATTRIBUTE_EDIT: 'attribute-edit',
  ATTRIBUTE_DELETE: 'attribute-delete',
  ATTRIBUTE_VALUE_CREATE: 'attribute-value-create',
  ATTRIBUTE_VALUE_EDIT: 'attribute-value-edit',
  ATTRIBUTE_VALUE_DELETE: 'attribute-value--delete'
};

interface RecentOrdersTableProps {
  className?: string;
}

const AttributesContent: FC<RecentOrdersTableProps> = () => {
  const { enhancedFetch } = useAuth();
  const updateState = useUpdateModalStore((state) => state);
  const [openAttributeValuesTable, setOpenAttributeValuesTable] =
    useState(false);
  const [reload, setReload] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [attributeId, setAttributeId] = useState('');
  const [searchData, setSearchData] = useState(null);
  const [sortBy, SetSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState<boolean>(false);

  // Call useAttributeForm at the component level
  const action = updateState.settings.sourceAction;
  const openModal =
    updateState.settings.modalOpen &&
    (action == SourceActions.ATTRIBUTE_CREATE ||
      action == SourceActions.ATTRIBUTE_EDIT);
  const apiMethod = action == SourceActions.ATTRIBUTE_EDIT ? 'PUT' : 'POST';
  const modalTitle =
    action == SourceActions.ATTRIBUTE_EDIT
      ? 'Update Attribute'
      : 'Create Attribute';
  const endpoint =
    action == SourceActions.ATTRIBUTE_EDIT
      ? updateState.settings.editEndpoint
      : `${API.HOST}/api/v1/attributes`;

  // Render CreateAttributeForm directly instead of using the hook
  const handleSubmit = (data) => {
    enhancedFetch(endpoint, {
      method: apiMethod,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributeName: data.attributeName
      })
    }).then(() => {
      updateState.actions.setModal({ modalOpen: false });
      setReload(!reload);
      updateState.actions.setModal({
        payload: {
          name: null
        },
        modalOpen: false
      });
    });
  };

  const handleClose = () => {
    updateState.actions.setModal({ modalOpen: false });
    updateState.actions.clearModal();
  };

  useEffect(() => {
    setLoading(true);
    enhancedFetch(`${API.HOST}/api/v1/attributes`, {
      // 1. Change the URL
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }).then((response) => {
      setLoading(false);
      if (Array.isArray(response)) {
        setAttributes(
          response?.map((attribute: any) => {
            return {
              id: attribute.id, // Change the Key and Value to map the response
              name: attribute.name,
              attributeValues: attribute.attributeValues
            };
          })
        );
      } else {
        console.warn("Expected type Array, got", typeof response);
      }
    });
  }, [reload]);

  useEffect(() => {
    const _attributes = attributes.sort((a, b) => {
      if (sortAsc === 'asc') {
        return descendingComparator(a, b, sortBy);
      }
      if (sortAsc === 'desc') {
        return -descendingComparator(a, b, sortBy);
      }
    });
    setAttributes(_attributes);
  }, [sortBy, sortAsc]);

  const renderCreateAttributeModal = () => {
    return (
      <CreateAttributeForm
        onSubmit={handleSubmit}
        onClose={handleClose}
        isLoading={false}
        title={modalTitle}
        openModal={openModal}
        fullWidth={true}
        maxWidth="sm"
        data={{
          attributeName: updateState.settings.payload?.name || ''
        }}
        editModal={action == SourceActions.ATTRIBUTE_EDIT}
        editData={updateState.settings.payload}
      />
    );
  };

  const renderCreateAttributeValueModal = () => {
    const attribute = attributes.find(
      (attribute: any) => attribute.id === attributeId
    );
    if (attribute == null) return;
    const action = updateState.settings.sourceAction;

    const openModal =
      updateState.settings.modalOpen &&
      (action == SourceActions.ATTRIBUTE_VALUE_CREATE ||
        action == SourceActions.ATTRIBUTE_VALUE_EDIT);
    const method =
      action == SourceActions.ATTRIBUTE_VALUE_EDIT ? 'PUT' : 'POST';
    const endpoint =
      action == SourceActions.ATTRIBUTE_VALUE_EDIT
        ? updateState.settings.editEndpoint
        : `${API.HOST}/api/v1/attribute-values`;

    // Handle form submission
    const handleSubmit = (data) => {
      enhancedFetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributeId: attribute.id,
          attributeValue: data.attributeValue
        })
      }).then(() => {
        updateState.actions.setModal({ modalOpen: false });
        setReload(!reload);
        updateState.actions.clearModal();
      }).catch((error) => {
        console.error(error);
        toast.error('Cannot create attribute value');
      });
    };

    // Handle modal close
    const handleClose = () => {
      updateState.actions.setModal({ modalOpen: false });
      updateState.actions.clearModal();
    };

    return (
      <CreateAttributeValueForm
        onSubmit={handleSubmit}
        onClose={handleClose}
        isLoading={false}
        title={`Attribute Value for ${attribute.name}`}
        openModal={openModal}
        fullWidth={true}
        maxWidth="sm"
        data={{
          attributeName: attribute.name,
          attributeId: attribute.id
        }}
        editModal={action == SourceActions.ATTRIBUTE_VALUE_EDIT}
        editData={updateState.settings.payload}
      />
    );
  };

  const renderAttributeValuesTableModal = () => {
    const attribute = attributes.find(
      (attribute: any) => attribute.id === attributeId
    );
    if (attribute == null) return;

    return (
      <div id={'view-attribute'}>
        {/*View Attribute Values */}
        <Dialog
          maxWidth={'lg'}
          open={openAttributeValuesTable}
          PaperProps={{ style: { zIndex: 1000 } }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            Attribute Values
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={(e) => {
              setOpenAttributeValuesTable(false);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                {/* eslint-disable-next-line react/jsx-no-undef */}
                <TableBody>
                  {attribute.attributeValues?.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="right">{row.id}</TableCell>
                      <TableCell align="right">{row.value}</TableCell>
                      <ActionCell
                        editEndpoint={`${API.HOST}/api/v1/attribute-values/${row.id}`}
                        deleteEndpoint={`${API.HOST}/api/v1/attribute-values/${row.id}`}
                        payload={row}
                        callback={() => setReload(!reload)}
                        sourceAction={{
                          EDIT: SourceActions.ATTRIBUTE_VALUE_EDIT
                        }}
                      />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button disabled={false} autoFocus onClick={(e) => {}}>
              {<CircularProgress size={6} />}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  };
  const sortedAttributes = [...attributes].sort((a, b) => {
    if (a.name > b.name) return 1; // Sort in ascending order
    if (a.name < b.name) return -1;
    return 0;
  })

  if (loading)
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LogoSpinner />
      </div>
    );

  const renderAttributesTable = () => {
    return (
      <TableContainer>
        <DynamicTable
          sortByField={sortBy}
          onSort={(headerProperty) => {
            SetSortBy(headerProperty);
            setSortAsc(sortAsc === 'asc' ? 'desc' : 'asc');
          }}
          sort={sortAsc}
          searchElement={
            <TableSearch
              placeholder={'Search Attributes'}
              searchData={attributes}
              setSearchData={setSearchData}
            />
          }
          rows={attributes?.map((item) => ({
            id: item.id,
            name: item.name,
            attributeValues: item.attributeValues
              ? item.attributeValues.map((value: any) => value.value).join(', ')
              : ''
          }))}
          headers={[

            {
              headerName: 'Name',
              field: 'name'
            },
            {
              headerName: 'Attribute Values',
              field: 'attributeValues',
              renderCell: ({row}) => (
                <>
                <Button
                  style={{ marginRight: 20 }}
                  onClick={(e) => {
                    setAttributeId(row.id);
                    updateState.actions.setModal({
                      modalOpen: true,
                      sourceAction: SourceActions.ATTRIBUTE_VALUE_CREATE
                    });
                  }}
                  variant={'contained'}
                >
                  Add Values
                </Button>
                  <Button
                    style={{ backgroundColor: '#FFA319' }}
                    onClick={(e) => {
                      setAttributeId(row.id);
                      setOpenAttributeValuesTable(true);
                    }}
                    variant={'contained'}
                  >
                    View Values
                  </Button>
                  </>
              )
            },
            {
              headerName: 'Actions',
              field: 'actions',
              renderCell: (value) => {
                return (
                  <ActionCell
                    editEndpoint={`${API.HOST}/api/v1/attributes/${value.row.id}`}
                    deleteEndpoint={`${API.HOST}/api/v1/attributes/${value.row.id}`}
                    payload={value.row}
                    callback={() => setReload(!reload)}
                    sourceAction={{
                      EDIT: SourceActions.ATTRIBUTE_EDIT
                    }}
                  />
                );
              }
            }
          ]}
        />

      </TableContainer>
    );
  };

  return (
    <Card>
      {renderCreateAttributeModal()}
      {renderCreateAttributeValueModal()}
      {renderAttributeValuesTableModal()}
      <Divider />
      {renderAttributesTable()}
    </Card>
  );
};

AttributesContent.propTypes = {
  className: PropTypes.string
};
export default AttributesContent;
